'use client';

import { createLogger } from '@svenskaspelab/nova-core/log';
import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { updateCouponBoard, updateCouponOutcome } from '@svenskaspelab/poolbetting/core/frameworks/actions/coupon';
import { useCouponBoard, useCouponId, useCouponIndices, useCouponType, useCouponUserOutcomes, useOutcomesSystemSize } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/coupon';
import { useDraw } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/draw';
import { useEngine } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/engine';
import { useGetQuickPick } from '@svenskaspelab/poolbetting/core/frameworks/nextjs/quickpick';
import { getOutcomeCount } from '@svenskaspelab/poolbetting/core/lib';
import type { AuthorProfile } from '@svenskaspelab/poolbetting/schema/content';
import type { Outcome } from '@svenskaspelab/poolbetting/schema/coupon';
import type { DrawNumber } from '@svenskaspelab/poolbetting/schema/draw';
import { usePathname } from 'next/navigation';
import { type ReactNode, type SubmitEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { buildSignsMap, isSizeDisabledForSigns } from '../utils/quickPickSizeRules';
import QuickPickForm from './QuickPickForm';
import { updateSrowCouponsWithQuickPick } from './updateSrowCouponsWithQuickPick';
const logger = createLogger('quick-pick-coupon');

// Recursively checks a (possibly nested) outcomes payload for any selected outcome number.
function hasOutcomeValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(item => hasOutcomeValue(item));
  }
  return typeof value === 'number';
}
type QuickPickClientContainerProps = {
  drawNumber: DrawNumber;
  authorGameAnalyses?: {
    authorProfile: AuthorProfile;
    selectedOutcomes?: Outcome[][][];
  }[];
};

/**
 * Quick pick form container component.
 *
 * @param root0 props
 * @param root0.drawNumber draw number of the current opened draw
 * @param root0.authorGameAnalyses game analysis data for current draw
 * @returns Quick pick form (presentational) component
 * @throws {Error} Will throw an error if draw number is not available
 */
export default function QuickPickClientContainer({
  drawNumber,
  authorGameAnalyses
}: QuickPickClientContainerProps): ReactNode {
  const {
    mutate,
    status
  } = useGetQuickPick();
  const isPending = status === 'PENDING';
  const isError = status === 'ERROR';
  const pathname = usePathname();
  const definition = useEngine();
  const defaultQuickPickSize = definition.model.defaultCouponValues.quickPickSize;
  const [quickPickSize, setQuickPickSize] = useState(defaultQuickPickSize);
  const [quickPickBase, setQuickPickBase] = useState<'ownChoice' | 'emptyCoupon' | `analysis-${string}`>('ownChoice');
  const [couponId, couponIndex] = useCouponId();
  const boardIndex = 0;
  const couponBoard = useCouponBoard({
    couponId,
    couponIndex,
    boardIndex
  });
  const chosenQuickPickSize = couponBoard?.quickPickSize;
  const couponType = useCouponType({
    couponId,
    couponIndex
  });
  const isSingleRow = couponType === 'singlerows';
  const couponIndices = useCouponIndices({
    couponId
  });
  const authorSelectedOutcomes = useMemo(() => {
    if (!authorGameAnalyses || !quickPickBase.startsWith('analysis-')) {
      return undefined;
    }
    return authorGameAnalyses.find(analysis => `analysis-${analysis.authorProfile.id}` === quickPickBase)?.selectedOutcomes;
  }, [authorGameAnalyses, quickPickBase]);
  const gameAnalysisOutcomesPayload = useMemo(() => authorSelectedOutcomes && authorSelectedOutcomes.length > 0 ? authorSelectedOutcomes : [], [authorSelectedOutcomes]);
  const authorProfiles = authorGameAnalyses?.map(analysis_0 => analysis_0.authorProfile) || [];
  const userOutcomesOptions = isSingleRow ? {
    couponId,
    boardIndex
  } : {
    couponId,
    couponIndex,
    boardIndex
  };
  const userOutcomesPayload = useCouponUserOutcomes(userOutcomesOptions);
  const userSystemSize = useOutcomesSystemSize({
    outcomes: userOutcomesPayload,
    isSingleRow: isSingleRow
  });
  const gameAnalysisSystemSize = useOutcomesSystemSize({
    outcomes: gameAnalysisOutcomesPayload,
    isSingleRow: false
  });
  const payload = quickPickBase === 'ownChoice' ? userOutcomesPayload : gameAnalysisOutcomesPayload;
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQuickPickSize(chosenQuickPickSize ?? defaultQuickPickSize);
  }, [chosenQuickPickSize, defaultQuickPickSize, setQuickPickSize]);
  const draw = useDraw({
    product: definition.moduleEngine,
    drawNumber
  });
  const sportType = draw.data.sport.type;
  const {
    eventType
  } = definition;
  const outcomeCount = getOutcomeCount(sportType, eventType, definition.model);
  const userSignsMap = useMemo(() => buildSignsMap(userOutcomesPayload), [userOutcomesPayload]);
  const authorSignsMap = useMemo(() => buildSignsMap(gameAnalysisOutcomesPayload), [gameAnalysisOutcomesPayload]);
  const hasUserOutcomes = useMemo(() => hasOutcomeValue(userOutcomesPayload), [userOutcomesPayload]);
  const shouldShowRadioGroup = hasUserOutcomes || authorGameAnalyses && authorGameAnalyses.length > 0;
  const isValueDisabled = useCallback((_size: number): boolean => {
    if (isSingleRow) {
      return false;
    }
    if (quickPickBase === 'emptyCoupon') {
      return false;
    }
    if (quickPickBase === 'ownChoice') {
      return isSizeDisabledForSigns({
        size: _size,
        systemSize: userSystemSize,
        signsMap: userSignsMap,
        outcomeCount
      });
    }
    if (quickPickBase.startsWith('analysis-')) {
      return isSizeDisabledForSigns({
        size: _size,
        systemSize: gameAnalysisSystemSize,
        signsMap: authorSignsMap,
        outcomeCount
      });
    }
    return false;
  }, [outcomeCount, quickPickBase, userSystemSize, userSignsMap, gameAnalysisSystemSize, authorSignsMap, isSingleRow]);
  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    trackEvent({
      category: 'Nya Tipsen - Fyll i med PIX!',
      action: 'Fyll i kupong',
      optLabel: pathname,
      optValue: `Önskat system - ${quickPickSize}`
    });
    mutate(isSingleRow ? {
      product: 'Powerplay',
      payload: quickPickBase === 'emptyCoupon' ? {} : payload,
      options: {
        type: {
          systemType: 'SROW',
          rowCount: quickPickSize
        },
        flavour: 'Safe1X2',
        drawNumber: drawNumber
      }
    } : {
      product: 'Powerplay',
      payload: quickPickBase === 'emptyCoupon' ? {} : payload,
      options: {
        type: {
          systemType: 'MSYS',
          size: quickPickSize
        },
        flavour: 'Safe1X2',
        drawNumber: drawNumber
      }
    }, {
      onSuccess: data => {
        if (isSingleRow) {
          const payloadOutcomes = data;
          updateSrowCouponsWithQuickPick({
            payloadOutcomes,
            couponIndices,
            couponId,
            couponType,
            boardIndex
          }).catch((error: unknown) => {
            logger.error('Updating SROW coupons with quick pick outcomes failed', {
              error,
              couponId,
              couponIndices
            });
          });
        } else {
          const [outcomes] = data;
          if (outcomes) {
            updateCouponOutcome(outcomes, {
              couponId,
              couponIndex,
              boardIndex
            }, 'keep').catch((error_0: unknown) => {
              logger.error('Updating coupon outcomes failed', {
                error: error_0,
                couponId,
                couponIndex,
                boardIndex
              });
            });
          }
        }
      },
      onError: err => {
        logger.error('QuickPick failed', {
          error: err,
          couponId,
          couponIndex,
          boardIndex,
          drawNumber
        });
      }
    });
    await updateCouponBoard({
      quickPickSize: quickPickSize
    }, {
      couponId,
      couponIndex,
      boardIndex
    });
  };
  const handleQuickPickSizeChange = (size: number): void => {
    if (size) {
      setQuickPickSize(size);
    }
  };
  return <QuickPickForm quickPickSelections={isSingleRow ? definition.model.quickPickSrow : definition.model.quickPickMsys} handleSubmit={handleSubmit} isPending={isPending} isError={isError} quickPickSize={quickPickSize} setQuickPickSize={handleQuickPickSizeChange} quickPickBase={quickPickBase} setQuickPickBase={setQuickPickBase} authorProfiles={authorProfiles} isValueDisabled={isValueDisabled} isSingleRow={isSingleRow} shouldShowRadioGroup={shouldShowRadioGroup ?? true} />;
}